// The privileged client only transports a validated Yakui tree, input
// events, client state and the answers to the UI's calls. Project
// JavaScript, React hooks, and callbacks run in the existing sandboxed script
// iframe, never in the client page.
//
// `pageState()` is what only the page knows (touch, pointer lock, the guide
// and link notes); `pageOps` are the calls only the page can carry out. Any
// other call goes to `game.ui_call`, which knows the rest and refuses
// anything else.
export function relayYakui(game, { pageState = () => ({}), pageOps = {} } = {}) {
    let frame = null, source = null, revision = 0, acknowledged = -1, lastSent = 0;
    let stopped = false, rustState = '{}', sentPage = null;
    const currentFrame = () => globalThis.sandbox_iframe?.contentWindow ?? null;
    const answer = (id, ok, value, error) => {
        currentFrame()?.postMessage({ type: 'yakui.reply', id, ok, value, error }, '*');
    };
    const call = async ({ id, op, args }) => {
        try {
            if (typeof op !== 'string' || (args != null && typeof args !== 'object')) throw new Error('malformed UI call');
            const value = Object.hasOwn(pageOps, op)
                ? await pageOps[op](args ?? {})
                : JSON.parse(game.ui_call(JSON.stringify({ ...(args ?? {}), op })));
            answer(id, true, value ?? null);
        } catch (error) {
            answer(id, false, null, String(error?.message ?? error));
        }
    };
    const onMessage = event => {
        if (stopped || event.source !== currentFrame()) return;
        const data = event.data;
        if (data?.type === 'yakui.tree' && data.revision === revision && typeof data.json === 'string') {
            try { game.set_scripted_ui(data.json); }
            catch (error) { console.error('Sandbox Yakui tree rejected:', error); }
        } else if (data?.type === 'yakui.sourceAck' && data.revision === revision) {
            acknowledged = revision;
            // A newly mounted UI is sent everything, not only what changes.
            game.resend_ui_state();
            sentPage = null;
        } else if (data?.type === 'yakui.call') {
            void call(data);
        }
    };
    window.addEventListener('message', onMessage);
    return {
        pump(time = Date.now() / 1000) {
            if (stopped) return;
            const nextFrame = currentFrame();
            if (nextFrame !== frame) {
                frame = nextFrame; acknowledged = -1; lastSent = 0;
                game.clear_scripted_ui();
            }
            const nextSource = game.scripted_ui_source();
            if (nextSource != null && nextSource !== source) {
                source = nextSource; revision++; acknowledged = -1; lastSent = 0;
            }
            if (frame && source != null && acknowledged !== revision && Date.now() - lastSent > 500) {
                frame.postMessage({ type: 'yakui.source', source, revision }, '*');
                lastSent = Date.now();
            }
            // Events are for the tree that was drawn; one not yet answered for
            // has no callbacks to meet them.
            const events = game.take_scripted_ui_events();
            const changed = game.take_ui_state();
            if (changed != null) rustState = changed;
            if (!frame || acknowledged !== revision) return;
            const page = JSON.stringify(pageState());
            let state = null;
            if (changed != null || page !== sentPage) {
                sentPage = page;
                state = rustState.length > 2 ? `{"page":${page},${rustState.slice(1)}` : `{"page":${page}}`;
            }
            frame.postMessage({ type: 'yakui.frame', revision, events, state, time }, '*');
        },
        stop() {
            if (stopped) return;
            stopped = true;
            window.removeEventListener('message', onMessage);
            frame?.postMessage({ type: 'yakui.source', source: null, revision: revision + 1 }, '*');
            game.clear_scripted_ui();
        },
    };
}
